<template>
  <div class="mobile-scanner">
    <!-- ===================== Top bar ===================== -->
    <header class="ms-topbar">
      <div class="ms-topbar-left">
        <button
          v-if="order"
          class="ms-icon-btn"
          @click="unloadOrder"
          title="Close current order"
        >
          <b-icon icon="x-lg"></b-icon>
        </button>

        <div class="ms-mode-toggle" role="group" aria-label="scan mode">
          <button
            :class="['ms-chip', !scanLocal ? 'ms-chip-active' : '']"
            @click="setMode(false)"
          >POLAND</button>
          <button
            :class="['ms-chip', scanLocal ? 'ms-chip-active ms-chip-local' : '']"
            @click="setMode(true)"
          >LOCAL</button>
        </div>
      </div>

      <div class="ms-topbar-right">
        <button
          class="ms-icon-btn"
          @click="openReadyList"
          title="Ready orders"
        >
          <b-icon icon="box-seam"></b-icon>
          <span v-if="readyCount > 0" class="ms-badge">{{ readyCount }}</span>
        </button>
      </div>
    </header>

    <!-- ===================== Body ===================== -->
    <main class="ms-main">
      <!-- Empty state -->
      <section v-if="!order" class="ms-empty">
        <div class="ms-empty-inner">
          <b-icon icon="upc-scan" class="ms-empty-icon"></b-icon>
          <h2 class="mt-3">Scan any item</h2>
          <p class="text-muted">
            The order it belongs to will appear here.<br />
            Mode: <strong>{{ scanLocal ? 'LOCAL' : 'POLAND' }}</strong>
          </p>
          <b-form @submit.prevent="manualScan" class="mt-4 w-100">
            <b-input-group>
              <b-form-input
                v-model="manualCode"
                placeholder="Type or paste EAN…"
                inputmode="numeric"
                autocomplete="off"
                class="ms-manual-input"
              />
              <b-input-group-append>
                <b-button type="submit" variant="primary">SCAN</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form>
        </div>
      </section>

      <!-- Loaded order -->
      <section v-else class="ms-order">
        <!-- Order header card -->
        <div :class="['ms-order-head', headerClass]">
          <div class="ms-order-head-row">
            <div class="ms-order-meta">
              <div class="ms-order-title">
                {{ order.metaData || 'WORKSHOP' }}
              </div>
              <div class="ms-order-sub">
                <span>{{ formatDate(order.addedAt) }}</span>
                <span v-if="customerName" class="ms-dot">•</span>
                <span v-if="customerName">{{ customerName }}</span>
              </div>
              <div v-if="deliveryName" class="ms-order-sub">
                <b-icon icon="truck"></b-icon> {{ deliveryName }}
              </div>
            </div>
            <button
              class="ms-box-btn"
              @click="openBoxAssignment"
            >
              <b-icon :icon="currentBox ? 'tags-fill' : 'tag'"></b-icon>
              <span class="ms-box-label">{{ currentBox || 'ASSIGN BOX' }}</span>
            </button>
          </div>

          <!-- Progress -->
          <div class="ms-progress">
            <div class="ms-progress-bar">
              <div
                class="ms-progress-fill"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
            <div class="ms-progress-label">
              {{ order.scannedCount }} / {{ order.orderedCount }} scanned
            </div>
          </div>
        </div>

        <!-- Articles -->
        <ul class="ms-articles">
          <li
            v-for="a in sortedArticles"
            :key="a._id"
            :class="[
              'ms-article',
              rowStateClass(a),
              a._id === lastScannedId ? 'ms-article-hl' : '',
            ]"
            :ref="a._id === lastScannedId ? 'lastScanned' : undefined"
          >
            <div class="ms-article-main">
              <div class="ms-article-name">{{ a.name }}</div>
              <div class="ms-article-meta">
                <span class="ms-badge-dark">{{ a.ssku }}</span>
                <span v-if="a.replacedWithLocal && a.lastLocation" class="ms-shelf">
                  <b-icon icon="geo-alt"></b-icon> {{ a.lastLocation }}
                </span>
                <span
                  v-if="a.replacedWithLocal"
                  class="ms-badge-dark ms-badge-warn"
                >LOCAL</span>
              </div>
              <div v-if="a.notes" class="ms-article-note">
                <b-icon icon="chat-left-text-fill"></b-icon>
                {{ stripHtmlTags(a.notes) }}
              </div>
            </div>
            <div class="ms-article-side">
              <div class="ms-article-count">
                <span class="ms-count-scanned">{{ a.confirmed || 0 }}</span>
                <span class="ms-count-sep">/</span>
                <span class="ms-count-total">{{ a.count }}</span>
              </div>
              <button
                class="ms-icon-btn ms-icon-sm"
                @click="openNotes(a)"
                title="Notes"
              >
                <b-icon icon="pencil-square"></b-icon>
              </button>
            </div>
          </li>
        </ul>
      </section>
    </main>

    <!-- ===================== Sticky close bar ===================== -->
    <div v-if="order && order.ready" class="ms-close-bar">
      <button class="ms-close-btn" @click="closePackage">
        <b-icon icon="box-seam" class="mr-2"></b-icon>
        CLOSE PACKAGE
      </button>
    </div>

    <!-- ===================== Loading overlay ===================== -->
    <div v-if="loading" class="ms-loader">
      <b-icon icon="arrow-clockwise" animation="spin" font-scale="3"></b-icon>
    </div>

    <!-- ===================== Notes modal ===================== -->
    <b-modal
      v-model="notesOpen"
      title="Note"
      centered
      ok-title="Save"
      cancel-title="Close"
      @ok.prevent="saveNotes"
    >
      <b-form-select
        v-model="currentNotes.noteType"
        :options="noteTypes"
        class="mb-3"
      />
      <b-form-textarea
        v-model="currentNotes.notes"
        rows="4"
        placeholder="Note text…"
      />
    </b-modal>

    <!-- ===================== Box select modal ===================== -->
    <b-modal
      id="ms-box-modal"
      v-model="boxModalOpen"
      :title="currentBox ? 'Change Packing Box' : 'Select Packing Box'"
      centered
      ok-title="Select"
      @ok.prevent="handleBoxSelection"
    >
      <div v-if="currentBox" class="mb-3 p-2 bg-warning text-dark rounded">
        <strong>Current:</strong> {{ currentBox }}
      </div>
      <v-select
        v-model="selectedBox"
        :options="boxOptions"
        :reduce="o => o.id"
        :searchable="false"
        placeholder="Please select available box…"
      >
        <template #option="option">
          <div :class="['ms-box-option', option.colorClass]">
            {{ option.boxName }}
          </div>
        </template>
        <template #selected-option="option">
          <div :class="['ms-box-option', option.colorClass]">
            {{ option.boxName }}
          </div>
        </template>
      </v-select>
    </b-modal>

    <!-- ===================== Ready orders sheet ===================== -->
    <div
      v-if="readySheetOpen"
      class="ms-sheet-backdrop"
      @click.self="readySheetOpen = false"
    >
      <div class="ms-sheet">
        <div class="ms-sheet-header">
          <h5 class="m-0">Ready to close</h5>
          <button class="ms-icon-btn" @click="readySheetOpen = false">
            <b-icon icon="x-lg"></b-icon>
          </button>
        </div>
        <div v-if="!readyGroups.length" class="ms-sheet-empty text-muted">
          No orders are fully scanned yet.
        </div>
        <ul v-else class="ms-sheet-list">
          <li
            v-for="g in readyGroups"
            :key="g.key"
            class="ms-sheet-item"
            @click="loadGroup(g.metaData, g.addedAt)"
          >
            <div>
              <div class="ms-sheet-meta">{{ g.metaData || 'WORKSHOP' }}</div>
              <div class="ms-sheet-sub">
                {{ formatDate(g.addedAt) }} • {{ g.scanned }}/{{ g.ordered }}
              </div>
            </div>
            <b-icon icon="chevron-right"></b-icon>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import 'moment-timezone';
import axios from 'axios';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import { metadataMapping, stripHtmlTags } from '@/utils';
import packageSticker from '@/sticker';
import fixed from '../fixed';

const SCAN_TIMEOUT_MS = 300;

export default {
  name: 'MobileScanner',
  components: { vSelect },
  data: () => ({
    loading: false,
    scanLocal: false,

    order: null, // { metaData, addedAt, fromWeb, articles, orderedCount, scannedCount, ready, matchedId }
    lastScannedId: null,

    // Scan buffer (handheld) + manual entry
    scanBuffer: '',
    lastScanTime: 0,
    manualCode: '',

    // Audio feedback
    successAudio: null,
    errorAudio: null,

    // Notes
    notesOpen: false,
    currentNotes: { id: '', noteType: 0, notes: '' },
    noteTypes: [
      { value: 0, text: 'Other' },
      { value: 1, text: 'EAN code issue' },
      { value: 2, text: 'Unavailable goods' },
      { value: 3, text: 'Goods replaced with' },
      { value: 4, text: 'Item canceled' },
    ],

    // Box assignment
    boxModalOpen: false,
    packingBoxes: [],
    selectedBox: null,

    // Ready-orders bottom sheet
    readySheetOpen: false,
    readyGroups: [],
    readyCount: 0,
  }),
  computed: {
    currentBox() {
      const b = this.firstBox();
      return b ? b.boxName : null;
    },
    currentBoxId() {
      const b = this.firstBox();
      return b ? b.id : null;
    },
    progressPercent() {
      if (!this.order || !this.order.orderedCount) return 0;
      return Math.min(100, Math.round((this.order.scannedCount / this.order.orderedCount) * 100));
    },
    customerName() {
      if (!this.order) return '';
      const withUser = this.order.articles.find((a) => a.webUser && a.webUser.name);
      return withUser ? withUser.webUser.name : '';
    },
    deliveryName() {
      if (!this.order) return '';
      const withDelivery = this.order.articles.find((a) => a.deliveryMethod && a.deliveryMethod.name);
      return withDelivery ? withDelivery.deliveryMethod.name : '';
    },
    headerClass() {
      if (!this.order || !this.order.articles.length) return 'ms-head-default';
      // Once every article is scanned, force a "ready" green header regardless
      // of what the delivery-method mapping would otherwise return. This gives
      // the operator an unmistakable visual signal that the order is ready to
      // close, and matches the color of the sticky CLOSE PACKAGE button.
      if (this.order.ready) return 'ms-head-ready';
      const first = this.order.articles[0];
      const mapped = metadataMapping.find((m) => m.pattern.test(first.metaData || ''));
      return mapped ? `ms-head-${mapped.getClass(first?.deliveryMethod?.name)}` : 'ms-head-default';
    },
    sortedArticles() {
      if (!this.order) return [];
      // Unconfirmed items first, then confirmed; among unconfirmed, local
      // items are sorted by shelf so pickers can walk the aisle top-down.
      const list = [...this.order.articles];
      return list.sort((a, b) => {
        const aDone = (a.confirmed || 0) >= a.count;
        const bDone = (b.confirmed || 0) >= b.count;
        if (aDone !== bDone) return aDone ? 1 : -1;
        if (a.replacedWithLocal && b.replacedWithLocal) {
          return this.compareShelf(a.lastLocation, b.lastLocation);
        }
        return 0;
      });
    },
    boxOptions() {
      return this.packingBoxes.map((p) => ({
        id: p.id,
        boxName: `${p.boxName} | Size: ${p.boxSize}`,
        colorClass: this.getBoxColorClass(p.boxSize),
      }));
    },
  },
  mounted() {
    this.initAudio();
    document.addEventListener('keydown', this.handleScannerKeys);
    document.addEventListener('paste', this.handlePaste);
    // Prefetch ready-count badge, non-blocking.
    this.refreshReadyGroups();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleScannerKeys);
    document.removeEventListener('paste', this.handlePaste);
    this.successAudio = null;
    this.errorAudio = null;
  },
  methods: {
    stripHtmlTags,

    /* ---------- helpers ---------- */
    firstBox() {
      if (!this.order) return null;
      const withBox = this.order.articles.find((a) => a.packingBox && a.packingBox.id);
      return withBox ? withBox.packingBox : null;
    },
    formatDate(d) {
      return moment.utc(d).tz('Europe/Oslo').format('DD.MM.YYYY HH:mm');
    },
    getBoxColorClass(size) {
      if (size === 2) return 'ms-opt-red';
      if (size === 3) return 'ms-opt-blue';
      return '';
    },
    rowStateClass(a) {
      const done = (a.confirmed || 0) >= a.count;
      const over = (a.confirmed || 0) > a.count;
      // Any note text OR any explicit noteType (>0) flags the item — apply
      // the matching background so it's obvious at a glance, exactly like
      // the old ProcessedOrders page did.
      const flagged = !!a.notes || (a.noteType && a.noteType > 0);
      if (flagged) {
        switch (a.noteType) {
          case 1: return 'ms-note-ean';
          case 2: return 'ms-note-unavailable';
          case 3: return 'ms-note-replaced';
          case 4: return 'ms-note-cancelled';
          default: return 'ms-note-generic';
        }
      }
      if (over) return 'ms-warn';
      if (done) return 'ms-ok';
      return '';
    },
    compareShelf(a, b) {
      const pa = this.parseShelf(a);
      const pb = this.parseShelf(b);
      const aValid = pa.letter !== null;
      const bValid = pb.letter !== null;
      if (aValid !== bValid) return aValid ? -1 : 1;
      if (!aValid) return 0;
      if (pa.number !== pb.number) return pa.number - pb.number;
      return pa.letter.localeCompare(pb.letter);
    },
    parseShelf(loc) {
      if (!loc) return { letter: null, number: null };
      const m = String(loc).trim().match(/^(\d+)\s*-\s*([A-Za-z])/);
      if (!m) return { letter: null, number: null };
      return { number: parseInt(m[1], 10), letter: m[2].toUpperCase() };
    },
    initAudio() {
      this.successAudio = new Audio('/ok.mp3');
      this.errorAudio = new Audio('/error.mp3');
      this.successAudio.preload = 'auto';
      this.errorAudio.preload = 'auto';
    },
    playOk() {
      if (this.successAudio) {
        this.successAudio.currentTime = 0;
        this.successAudio.play().catch(() => {});
      }
    },
    playErr() {
      if (this.errorAudio) {
        this.errorAudio.currentTime = 0;
        this.errorAudio.play().catch(() => {});
      }
    },

    /* ---------- mode + input handling ---------- */
    setMode(local) {
      this.scanLocal = local;
    },
    unloadOrder() {
      // Drop the currently loaded order and go back to the idle "waiting for
      // scan" screen. Nothing is saved / cleared server-side – scanned counts
      // and box assignments persist and reappear as soon as the operator
      // scans any item from this order (or picks it from the ready list).
      this.order = null;
      this.lastScannedId = null;
      this.scanBuffer = '';
    },
    handleScannerKeys(event) {
      // Ignore key events while typing in inputs / textareas so manual
      // entry, notes modal etc. keep working normally.
      const t = event.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      const now = Date.now();
      const gap = now - this.lastScanTime;
      if (gap > SCAN_TIMEOUT_MS) {
        this.scanBuffer = '';
        if (event.key.length === 1) this.scanBuffer = event.key;
      } else if (event.key === 'Enter') {
        if (this.scanBuffer.length > 0) {
          const code = this.scanBuffer;
          this.scanBuffer = '';
          this.scan(code);
        }
        event.preventDefault();
      } else if (event.key.length === 1) {
        this.scanBuffer += event.key;
      }
      this.lastScanTime = now;
    },
    manualScan() {
      if (!this.manualCode) return;
      const code = this.manualCode.trim();
      this.manualCode = '';
      this.scan(code);
    },
    // Global paste handler. Handheld scanners emit per-keystroke events that
    // `handleScannerKeys` already buffers, but developers / operators often
    // paste an EAN with cmd/ctrl+v to test the flow. A paste arrives as one
    // event so the per-keystroke buffer never sees it — handle it here.
    handlePaste(event) {
      const t = event.target;
      // Let paste behave normally inside real inputs (notes textarea, manual
      // scan input, box search box, …). Those inputs trigger `scan()` on
      // their own submit path.
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      const text = (event.clipboardData || window.clipboardData)?.getData('text');
      if (!text) return;
      const code = text.trim();
      if (!code) return;
      event.preventDefault();
      this.scan(code);
    },

    /* ---------- scan flow ---------- */
    async scan(code) {
      if (!code) return;
      this.loading = true;
      try {
        const { data } = await axios.put(
          `${fixed.API_URL}/admin/mobile-scan`,
          { code, local: this.scanLocal },
          { withCredentials: true },
        );

        const isNewOrder = !this.order
          || this.order.metaData !== data.metaData
          || moment.utc(this.order.addedAt).format('YYYY-MM-DD')
            !== moment.utc(data.addedAt).format('YYYY-MM-DD');

        this.order = data;
        this.lastScannedId = data.matchedId || null;

        this.playOk();

        // First scan of a new order + no box yet → prompt to assign one.
        // The single-line "first scan" case (scannedCount === 1) is
        // detected server-side by the aggregated payload.
        if (isNewOrder && !this.currentBox && data.scannedCount === 1) {
          await this.openBoxAssignment(true);
        }

        this.$nextTick(() => this.scrollHighlighted());
        this.refreshReadyGroups(); // update badge in background
      } catch (error) {
        this.playErr();
        const msg = error?.response?.data || error?.message || 'scan failed';
        this.$toast.error(msg);
      } finally {
        this.loading = false;
      }
    },
    scrollHighlighted() {
      const el = this.$refs.lastScanned && this.$refs.lastScanned[0];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },

    /* ---------- order group loading ---------- */
    async loadGroup(metaData, addedAt) {
      this.loading = true;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/order-group`, {
          params: { metaData, addedAt },
          withCredentials: true,
        });
        this.order = data;
        this.lastScannedId = null;
        this.readySheetOpen = false;
      } catch (error) {
        this.$toast.error(error?.response?.data || 'failed to load order');
      } finally {
        this.loading = false;
      }
    },
    async refreshCurrentOrder() {
      if (!this.order) return;
      try {
        const { data } = await axios.get(`${fixed.API_URL}/admin/order-group`, {
          params: { metaData: this.order.metaData, addedAt: this.order.addedAt },
          withCredentials: true,
        });
        this.order = data;
      } catch (error) {
        // If the group is gone (all packed), clear the view.
        if (error?.response?.status === 404) this.order = null;
      }
    },

    /* ---------- ready orders list ---------- */
    async openReadyList() {
      this.readySheetOpen = true;
      await this.refreshReadyGroups();
    },
    async refreshReadyGroups() {
      try {
        const { data } = await axios.get(
          `${fixed.API_URL}/poland-processing`,
          { withCredentials: true },
        );
        // Group by metaData + day, count ordered/scanned, keep only ready groups.
        const map = new Map();
        (data || []).forEach((row) => {
          const day = moment.utc(row.addedAt).format('YYYY-MM-DD');
          const key = `${(row.metaData || '').trim()}|${day}`;
          const g = map.get(key) || {
            key,
            metaData: (row.metaData || '').trim(),
            addedAt: row.addedAt,
            ordered: 0,
            scanned: 0,
          };
          g.ordered += row.count || 0;
          g.scanned += row.confirmed || 0;
          // keep earliest addedAt for stable rendering
          if (new Date(row.addedAt) < new Date(g.addedAt)) g.addedAt = row.addedAt;
          map.set(key, g);
        });
        const ready = [...map.values()].filter((g) => g.ordered > 0 && g.ordered === g.scanned);
        ready.sort((a, b) => new Date(a.addedAt) - new Date(b.addedAt));
        this.readyGroups = ready;
        this.readyCount = ready.length;
      } catch (error) {
        // silent – the badge is a nice-to-have, not critical.
      }
    },

    /* ---------- notes ---------- */
    openNotes(article) {
      this.currentNotes = {
        // eslint-disable-next-line no-underscore-dangle
        id: article._id,
        noteType: article.noteType || 0,
        notes: article.notes || '',
      };
      this.notesOpen = true;
    },
    async saveNotes() {
      if (!this.currentNotes.id) return;
      this.loading = true;
      try {
        await axios.post(
          `${fixed.API_URL}/poland-order-comment`,
          this.currentNotes,
          { withCredentials: true },
        );
        this.$toast.success('Note saved');
        this.notesOpen = false;
        await this.refreshCurrentOrder();
      } catch (error) {
        this.$toast.error(error?.response?.data || "can't save note");
      } finally {
        this.loading = false;
      }
    },

    /* ---------- box assignment ---------- */
    async openBoxAssignment(isFirstScan = false) {
      if (!this.order) return;
      this.selectedBox = null;
      try {
        const { data } = await axios.get(
          `${fixed.API_URL}/admin/pack-boxes`,
          { withCredentials: true },
        );
        this.packingBoxes = (data || []).filter((b) => !b.occupied);
      } catch (error) {
        this.$toast.error('Failed to fetch packing boxes');
        return;
      }
      this.boxModalOpen = true;
      // eslint-disable-next-line no-unused-expressions
      isFirstScan; // parameter kept for future contextual differences
    },
    async handleBoxSelection() {
      if (!this.selectedBox) {
        this.$toast.warning('Please select a box');
        return;
      }
      if (!this.order) return;
      this.loading = true;
      try {
        const payload = {
          boxID: this.selectedBox,
          metaData: this.order.metaData,
          addedAt: this.order.addedAt,
        };
        if (this.currentBoxId) payload.oldBoxID = this.currentBoxId;

        const { data } = await axios.put(
          `${fixed.API_URL}/admin/occupy-box`,
          payload,
          { withCredentials: true },
        );
        this.$toast.success(`Box ${data.boxName} assigned`);
        this.boxModalOpen = false;
        await this.refreshCurrentOrder();
      } catch (error) {
        this.$toast.error(error?.response?.data || 'Failed to assign box');
      } finally {
        this.loading = false;
      }
    },

    /* ---------- close package ---------- */
    async closePackage() {
      if (!this.order || !this.order.ready) return;
      this.loading = true;
      try {
        const payload = {
          fromWeb: !!this.order.fromWeb,
          metaData: this.order.metaData,
          addedAt: this.order.addedAt,
        };
        if (this.currentBoxId) payload.boxId = this.currentBoxId;

        const { data } = await axios.put(
          `${fixed.API_URL}/admin/close-package`,
          payload,
          { withCredentials: true },
        );

        // The package is closed either way, so a failed shipping label comes
        // back alongside a success. It must not auto-dismiss next to the
        // "Package closed" toast – without a label the parcel cannot ship.
        if (data && data.shippingError) {
          this.$toast.warning(
            `No shipping label: ${data.shippingError}. Create it manually before sending the parcel.`,
            { timeout: false },
          );
        }

        // Edge case: every remaining article is canceled / unavailable
        // (server strips noteType 2 and 4 from the sticker payload). Printing
        // a blank sticker helps nobody – just close the order and move on.
        const products = (data && data.products) || [];
        if (products.length === 0) {
          this.$toast.success('Order closed (no items to print)');
          this.order = null;
          this.lastScannedId = null;
          this.refreshReadyGroups();
          return;
        }

        // Freeze the sticker payload for the same reason ProcessedOrders did.
        const stickerData = Object.freeze({
          ...data,
          products: Object.freeze(products.map((p) => Object.freeze({ ...p }))),
        });
        const iframe = packageSticker(stickerData);
        iframe.onload = () => {
          this.$nextTick(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            document.body.removeChild(iframe);
          });
        };

        this.$toast.success('Package closed');
        this.order = null;
        this.lastScannedId = null;
        this.refreshReadyGroups();
      } catch (error) {
        this.$toast.error(error?.response?.data || "Can't close package");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<!-- Not scoped: several classes are applied on portalled Bootstrap-Vue modals
     (box-select v-select dropdown) which mount outside this component's tree,
     and other classes are bound dynamically. All selectors are `ms-` prefixed
     so leakage risk is minimal. -->
<style>
/* ============ layout ============ */
.mobile-scanner {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  padding-bottom: 96px; /* space for close bar */
  -webkit-user-select: none;
  user-select: none;
  font-size: 15px;
  font-family: var(--font);
  /* Keep the reading width comfortable on tablets / desktops. The layout
     is still mobile-first – on phones the max-width has no effect. */
  max-width: 720px;
  margin: 0 auto;
  /* Fixed so the sticky order header knows where to sit below it. */
  --ms-topbar-h: 65px;
}

/* ============ top bar ============ */
.ms-topbar {
  position: sticky;
  /* Published by Navbar.vue; 0 when the navbar isn't rendered. */
  top: var(--navbar-height, 0px);
  z-index: 5;
  height: var(--ms-topbar-h);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(6px);
}
.ms-topbar-left,
.ms-topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ms-icon-btn {
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 10px 12px;
  font-size: 1.15rem;
  line-height: 1;
  position: relative;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.ms-icon-btn:hover { background: var(--surface-3); border-color: var(--border-strong); }
.ms-icon-btn:active { background: var(--surface-3); }
.ms-icon-sm { min-width: 40px; min-height: 40px; padding: 8px 10px; }
.ms-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: var(--warning);
  color: #111;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 6px;
  min-width: 20px;
  text-align: center;
}
.ms-mode-toggle {
  display: inline-flex;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 2px;
}
.ms-chip {
  background: transparent;
  color: var(--text-muted);
  border: none;
  padding: 8px 14px;
  border-radius: var(--radius-sm);
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.03em;
}
.ms-chip-active { background: var(--accent); color: #fff; }
.ms-chip-local  { background: var(--info); }

/* ============ empty state ============ */
.ms-main { padding: 12px; }
.ms-empty {
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.ms-empty-inner { max-width: 420px; width: 100%; }
.ms-empty-icon { font-size: 5rem; color: var(--text-faint); }
.ms-manual-input {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--border);
}

/* ============ order card ============ */
.ms-order-head {
  /* Kept in view while scrolling long article lists. */
  position: sticky;
  top: calc(var(--navbar-height, 0px) + var(--ms-topbar-h));
  z-index: 4;
  border-radius: var(--radius-lg);
  padding: 14px;
  margin-bottom: 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--text);
  box-shadow: var(--shadow-sm);
}
.ms-order-head-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.ms-order-title {
  font-size: 1.15rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.ms-order-sub {
  font-size: 0.85rem;
  opacity: 0.85;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.ms-dot { opacity: 0.5; }
.ms-box-btn {
  background: rgba(0, 0, 0, 0.25);
  color: inherit;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: var(--radius);
  padding: 8px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  max-width: 45%;
  text-align: left;
}
.ms-box-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 130px;
}

/* Header color variants driven by utils/metadataMapping.getClass.
   These are accent bands used on the *order header card* only. */
.ms-head-default    { background: var(--surface-2); }
.ms-head-ready      { background: var(--success); color: #fff; }
.ms-head-bg-info    { background: var(--info); color: #fff; }
.ms-head-bg-success { background: var(--success); color: #fff; }
.ms-head-bg-danger  { background: var(--danger); color: #fff; }
.ms-head-regos-tape { background: #6f42c1; color: #fff; }
.ms-head-pickup-location { background: var(--warning); color: #111; }

/* progress */
.ms-progress { margin-top: 12px; }
.ms-progress-bar {
  height: 8px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  overflow: hidden;
}
.ms-progress-fill {
  height: 100%;
  background: #fff;
  transition: width 200ms ease;
}
.ms-progress-label {
  margin-top: 4px;
  font-size: 0.8rem;
  opacity: 0.9;
}
/* When the header card is on the neutral surface (no order-type color), give
   the progress a bit more contrast using the accent palette. */
.ms-head-default .ms-progress-bar { background: rgba(255, 255, 255, 0.08); }
.ms-head-default .ms-progress-fill { background: var(--accent); }

/* ============ articles ============ */
.ms-articles {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ms-article {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 12px;
  border-left: 4px solid var(--border);
  transition: background 200ms ease, border-color 200ms ease;
}
.ms-article-main { flex: 1; min-width: 0; }
.ms-article-name {
  font-weight: 700;
  font-size: 0.98rem;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text);
}
.ms-article-meta {
  margin-top: 4px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 0.75rem;
}
.ms-badge-dark {
  background: var(--bg);
  color: var(--text-muted);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 2px 8px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.ms-badge-warn { background: var(--warning); color: #111; border-color: transparent; }
.ms-shelf {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--warning);
  font-weight: 700;
}
.ms-article-note {
  margin-top: 6px;
  font-size: 0.78rem;
  color: var(--text);
  background: rgba(0, 0, 0, 0.25);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  display: inline-flex;
  gap: 6px;
  align-items: center;
}
.ms-article-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 6px;
  min-width: 60px;
}
.ms-article-count {
  font-weight: 800;
  font-size: 1.15rem;
  line-height: 1;
}
.ms-count-scanned { color: var(--text); }
.ms-count-sep { opacity: 0.5; margin: 0 2px; }
.ms-count-total { opacity: 0.7; }

/* state variants – tinted using the theme palette so they blend in with
   the surrounding surfaces instead of shouting like standalone banners. */
.ms-article.ms-ok {
  background: rgba(52, 185, 121, 0.12);
  border-color: rgba(52, 185, 121, 0.35);
  border-left-color: var(--success);
}
.ms-article.ms-ok .ms-count-scanned { color: #7ee6a8; }
.ms-article.ms-warn {
  background: rgba(242, 176, 44, 0.12);
  border-color: rgba(242, 176, 44, 0.35);
  border-left-color: var(--warning);
}
.ms-article.ms-note-ean {
  background: rgba(160, 82, 45, 0.14);
  border-color: rgba(160, 82, 45, 0.4);
  border-left-color: #a0522d;
}
.ms-article.ms-note-unavailable {
  background: rgba(111, 66, 193, 0.16);
  border-color: rgba(111, 66, 193, 0.4);
  border-left-color: #6f42c1;
}
.ms-article.ms-note-replaced {
  background: rgba(242, 176, 44, 0.16);
  border-color: rgba(242, 176, 44, 0.4);
  border-left-color: var(--warning);
}
.ms-article.ms-note-cancelled {
  background: rgba(229, 72, 77, 0.12);
  border-color: rgba(229, 72, 77, 0.35);
  border-left-color: var(--danger);
  opacity: 0.75;
}
.ms-article.ms-note-cancelled .ms-count-scanned { color: #f5b7b7; }
.ms-article.ms-note-generic {
  background: var(--accent-soft);
  border-color: rgba(79, 140, 255, 0.35);
  border-left-color: var(--accent);
}

/* highlighted last-scanned row */
.ms-article.ms-article-hl {
  outline: 2px solid var(--accent);
  outline-offset: 0;
  transform: scale(1.005);
  box-shadow: 0 6px 20px rgba(79, 140, 255, 0.25);
}

/* ============ close bar ============ */
.ms-close-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(to top, var(--bg), rgba(20, 22, 27, 0.85));
  z-index: 6;
}
.ms-close-btn {
  width: 100%;
  max-width: 696px; /* mobile-scanner max-width minus horizontal padding */
  display: block;
  margin: 0 auto;
  padding: 16px;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  background: var(--success);
  color: #fff;
  border: none;
  border-radius: var(--radius-lg);
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(52, 185, 121, 0.35);
}
.ms-close-btn:hover { filter: brightness(1.05); }
.ms-close-btn:active { filter: brightness(0.9); }

/* ============ loader overlay ============ */
.ms-loader {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  color: var(--text);
}

/* ============ box option colours (vue-select) ============ */
.ms-box-option {
  padding: 4px 8px;
  border-radius: var(--radius-sm);
}
.ms-opt-red  { background: #ffcccc; color: #cc0000; }
.ms-opt-blue { background: #cce0ff; color: #0044cc; }

/* ============ ready orders sheet ============ */
.ms-sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 20;
}
.ms-sheet {
  background: var(--surface);
  border: 1px solid var(--border);
  border-bottom: none;
  color: var(--text);
  width: 100%;
  max-width: 560px;
  border-top-left-radius: var(--radius-lg);
  border-top-right-radius: var(--radius-lg);
  padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
}
.ms-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px 12px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 8px;
}
.ms-sheet-empty {
  padding: 30px 10px;
  text-align: center;
  color: var(--text-muted);
}
.ms-sheet-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.ms-sheet-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 10px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
}
.ms-sheet-item:hover { background: var(--surface-2); }
.ms-sheet-item:active { background: var(--surface-3); }
.ms-sheet-meta { font-weight: 700; }
.ms-sheet-sub { font-size: 0.8rem; color: var(--text-muted); }
</style>
